import { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const Slide = ({ slide, onEnded, single }) =>
  slide.type === "video" ? (
    <video
      src={slide.url}
      className="w-full h-full object-cover"
      autoPlay
      muted
      loop={single}
      playsInline
      onEnded={onEnded}
      data-testid="hero-video"
    />
  ) : (
    <img src={slide.url} alt="TheMatheMind students learning" className="w-full h-full object-cover" data-testid="hero-slide-image" />
  );

export const HeroCaption = ({ slide, onButton }) => {
  if (!slide.headline && !slide.subtext && !slide.button_label) return null;
  return (
    <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 via-black/25 to-transparent pointer-events-none" data-testid="hero-caption">
      <motion.div
        className="p-5 pb-12 sm:p-10 sm:pb-14 max-w-2xl text-left"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        {slide.headline && <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-['Manrope'] leading-tight drop-shadow" data-testid="hero-caption-headline">{slide.headline}</h2>}
        {slide.subtext && <p className="mt-2 text-sm sm:text-lg text-white/90 drop-shadow" data-testid="hero-caption-subtext">{slide.subtext}</p>}
        {slide.button_label && slide.button_link && (
          <button
            onClick={(e) => { e.stopPropagation(); onButton(slide.button_link); }}
            className="pointer-events-auto mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold px-6 h-11 shadow-lg transition-[transform,background-color] duration-200 hover:-translate-y-0.5"
            data-testid="hero-caption-button"
          >
            {slide.button_label} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </motion.div>
    </div>
  );
};

export const HeroCarousel = ({ slides, interval = 5, onOpen }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();
  const followLink = (link) => {
    if (link.startsWith("/")) navigate(link);
    else if (link.startsWith("#")) document.querySelector(link)?.scrollIntoView({ behavior: "smooth" });
    else if (link.startsWith("http")) window.open(link, "_blank", "noopener");
    else window.location.href = link;
  };
  const count = slides.length;
  const current = slides[index % count];

  const go = useCallback((step) => setIndex((i) => (i + step + count) % count), [count]);

  useEffect(() => {
    clearTimeout(timer.current);
    // videos advance when they finish; images after the interval
    if (count > 1 && !paused && current?.type !== "video") {
      timer.current = setTimeout(() => go(1), interval * 1000);
    }
    return () => clearTimeout(timer.current);
  }, [index, paused, count, interval, current, go]);

  if (!current) return null;

  const arrow = "absolute top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity";

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-2xl cursor-pointer group h-[260px] sm:h-[400px] bg-muted"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => onOpen(current)}
      data-testid="hero-image-clickable"
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={current.id || index}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          data-testid={`hero-slide-${index}`}
        >
          <Slide slide={current} single={count === 1} onEnded={() => go(1)} />
          <HeroCaption slide={current} onButton={followLink} />
        </motion.div>
      </AnimatePresence>
      {count > 1 && (
        <>
          <button className={`${arrow} left-3`} onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous slide" data-testid="hero-prev">
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
          <button className={`${arrow} right-3`} onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next slide" data-testid="hero-next">
            <ChevronRight className="w-5 h-5 text-foreground" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2" data-testid="hero-dots">
            {slides.map((s, i) => (
              <button
                key={s.id || i}
                onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                className={`h-2 rounded-full transition-[width,background-color] duration-300 ${i === index ? "w-6 bg-white" : "w-2 bg-white/60 hover:bg-white/90"}`}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
